import { createStore, compose, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { actionGetRates } from '../actions/rates';
import reducers from '../reducers/index';
import accountsSaga from '../sagas/accountsSaga';
import ratesSaga from '../sagas/ratesSaga';
import modalSearchSaga from '../sagas/modalSearchSaga';
import transactionsSaga from '../sagas/transactionsSaga';
import layoutSaga from '../sagas/layoutSaga';
import limitOrdersSaga from '../sagas/limitOrdersSaga';
import authSaga from '../sagas/authSaga';
import watchListSaga from '../sagas/watchlistSaga';
import cexSaga from '../sagas/cexSaga';
import cexOrdersSaga from '../sagas/cexOrdersSaga';
import walletWatchlistSaga from '../sagas/walletWatchlistSaga';
import paymentSaga from '../sagas/paymentSaga';
import walletTagsSaga from '../sagas/walletTagsSaga';
import BlocksSubscription from '../core/BlocksSubscription';
import { actionCreateNotification } from '../actions/notifications';
import { actionShowModal } from '../actions/modal';
import { ModalTypes } from '../reducers/modal';
import { IPriceAlert, ILimitOrder, APIs } from '@kattana-io/common';
import priceAlertsSaga from '../sagas/priceAlertsSaga';
import { actionTriggerPriceAlert } from '../actions/price_alerts';
import playPopSound from '../assets/sounds/popSound';
import { actionTriggerLimitOrder } from '../actions/limit_orders';
import { setupDecoders } from '../core/proto-load';
import { actionRegistryLoad } from '../actions/registry';
import registrySaga from '../sagas/registrySaga';
import { actionUpdateCexBalance } from '../actions/cex_trading';
import { actionUpdateCexOrder } from '../actions/cex_orders';
import { ECEXOrderStatus, ICEXOrderUpdate } from '../reducers/cex_orders';
import { IExtendedCEXBalanceItem } from '../reducers/cex_trading';

const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.kattana.trade';
const apiUrl = process.env.REACT_APP_API_URL || 'https://api.kattana.trade';
const AuthAPI = new APIs.auth.Auth(authUrl);
const KattanaAPI = new APIs.kattana.KattanaApi(apiUrl);
const CexAPI = new APIs.cex.CexApi(apiUrl);
const UserAPI = new APIs.user.UserApi(apiUrl);
const LimitOrderAPI = new APIs.limitOrder.LimitOrderApi(apiUrl);
const WatchListAPI = new APIs.watchlist.WatchListApi(apiUrl);
const PriceAlertAPI = new APIs.priceAlert.PriceAlertApi(apiUrl);
const RegistryAPI = new APIs.registry.Registry(apiUrl);
const WalletAPI = new APIs.wallet.WalletApi(apiUrl);

const sagaMiddleware = createSagaMiddleware();

const composeEnhancers =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

setupDecoders();

const store = createStore(
  reducers,
  composeEnhancers(applyMiddleware(sagaMiddleware))
);

sagaMiddleware.run(accountsSaga);
sagaMiddleware.run(ratesSaga);
sagaMiddleware.run(modalSearchSaga);
sagaMiddleware.run(layoutSaga);
sagaMiddleware.run(priceAlertsSaga);
sagaMiddleware.run(limitOrdersSaga);
sagaMiddleware.run(authSaga);
sagaMiddleware.run(watchListSaga);
sagaMiddleware.run(transactionsSaga);
sagaMiddleware.run(cexSaga);
sagaMiddleware.run(cexOrdersSaga);
sagaMiddleware.run(registrySaga);
sagaMiddleware.run(walletWatchlistSaga);
sagaMiddleware.run(paymentSaga);
sagaMiddleware.run(walletTagsSaga);

store.dispatch(actionRegistryLoad());
store.dispatch(actionGetRates());

!navigator.onLine && store.dispatch(actionShowModal(ModalTypes.offline, {}));

/**
 * Subscribe to web3 events
 * React to each new ETH block mined
 */
const subscription = new BlocksSubscription({
  newpairs: (message: string) =>
    store.dispatch(actionCreateNotification({ message })),
  priceAlerts: (alert: IPriceAlert) => {
    playPopSound();
    store.dispatch(actionTriggerPriceAlert(alert));
  },
  limitOrders: (limitOrder: ILimitOrder) => {
    playPopSound();
    store.dispatch(actionTriggerLimitOrder(limitOrder));
  },
  updateCexBalance: (data: IExtendedCEXBalanceItem) =>
    store.dispatch(actionUpdateCexBalance(data)),
  updateCexOrder: (data: ICEXOrderUpdate) => {
    store.dispatch(actionUpdateCexOrder(data));
    if (data.state.status == ECEXOrderStatus.FILLED) {
      playPopSound();
    }
  },
});

export {
  store,
  subscription,
  apiUrl,
  AuthAPI,
  KattanaAPI,
  CexAPI,
  UserAPI,
  LimitOrderAPI,
  WatchListAPI,
  PriceAlertAPI,
  RegistryAPI,
  WalletAPI,
};
